import React, { ChangeEvent, useEffect } from 'react';
import { Button, Form, Input, Select } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import { EnumClosingCostType } from '../../../../types/settings';
import {
  useLoanClosingCostUpdate,
  useReportClosingCostCreate,
  useReportLoansClosingCosts, useReportLoanUpdate
} from '../../../../hooks/reports';
import { useAuth } from '../../../../context/auth';
import { useContextReports } from '../../../../context/reports';
import { useContextNotifications } from '../../../../context/notifications';
import { ILoanValue } from '../../../../types/reports';
import { updateAprAndAprRelatedClosingCost } from '../../../../utils/updateAprAndAprRelatedClosingCost';
import { calculateLoanClosingCostAmount } from '../../../../utils/calculateLoanClosingCostAmount';
import { prepareLoanDataFromForm } from '../../../../utils/prepareData';
import { formatAmount } from '../../../../utils/formatAmount';
import { calculateLoanAmountWithUip } from '../../../../utils/calculateLoanAmountWithUip';
import { calculateDownPaymentPrice } from '../../../../utils/calculateDownPaymentPrice';
import { calculatePrincipalInterest } from '../../../../utils/calculatePrincipalInterest';

const { Option } = Select;

type ClosingCostFormPopoverProps = {
  reportId: string;
  updateRows?: {
    text: string;
    amount: number;
    closingCostId: number;
    editable: boolean;
    type: EnumClosingCostType;
    propertyTaxesMonthsCollected: number;
    daysCollected: number;
  }[];
  setPopoverOpen?: (open: boolean) => void;
  refetchData?: () => void;
  isEditMode?: boolean;
  // eslint-disable-next-line react/require-default-props
  rowsInitializedRef?: React.MutableRefObject<boolean>;
  isPopoverOpen?: boolean;
}

const ClosingCostFormPopover: React.FC<ClosingCostFormPopoverProps> = ({
  reportId,
  updateRows,
  setPopoverOpen,
  refetchData,
  isEditMode,
  rowsInitializedRef,
  isPopoverOpen
}) => {
  const formInstance = Form.useFormInstance()
  const [form] = useForm();
  const { user } = useAuth();
  const { fetch: createReportClosingCost, loading: createLoading } = useReportClosingCostCreate();
  const { fetch: updateLoanClosingCost, loading: updateLoading } = useLoanClosingCostUpdate();
  const { fetch: fetchLoansClosingCosts } = useReportLoansClosingCosts();
  const { reportClosingCosts, getLoans, setPreviewLoading } = useContextReports()
  const { openNotification } = useContextNotifications();
  const { fetch: reportLoanUpdate } = useReportLoanUpdate();

  useEffect(() => {
    if (!reportClosingCosts || !updateRows || !isEditMode) return

    reportClosingCosts.forEach(cost => {
      if (cost.text === updateRows[0].text && cost.type !== EnumClosingCostType.prepaidInterest15Days) {
        form.setFieldsValue({
          amount: cost.amount
        })
      }
    })
  }, [reportClosingCosts])

  useEffect(() => {
    if (updateRows && updateRows.length > 0) {
      const closingCost = updateRows[0];

      form.setFieldsValue({
        text: closingCost.text,
        type: closingCost.type,
        propertyTaxesMonthsCollected: closingCost.propertyTaxesMonthsCollected,
        daysCollected: closingCost.daysCollected
      });
    }
  }, [isPopoverOpen]);

  const onEdit = async (
    id: string | number,
    oldAmount: number,
    index: number,
    values: {
      text: string;
      type: EnumClosingCostType;
      amount?: number;
      propertyTaxesMonthsCollected?: number;
      daysCollected?: number;
    }) => {
    if (!reportId) return;
    if (!user.id) return;

    try {
      setPreviewLoading(true)

      const dataToSend: any = {
        text: values.text,
        type: values.type,
        ...(values?.propertyTaxesMonthsCollected &&
          { propertyTaxesMonthsCollected: values.propertyTaxesMonthsCollected }),
        ...(values?.daysCollected && { daysCollected: values.daysCollected }),
        reportId
      };

      if (
        values.amount ||
        values.amount === 0 ||
        values.type === EnumClosingCostType.propertyTaxes ||
        values.type === EnumClosingCostType.homeownersInsurance ||
        values.type === EnumClosingCostType.prepaidInterest15Days
      ) {
        dataToSend.amount = values.amount;
        const loans = formInstance.getFieldValue(['loans', index]);
        const closingCosts = parseFloat(formInstance.getFieldValue(['loans', index, 'closingCosts', 'value'])) || 0;

        const amount = calculateLoanClosingCostAmount(
          loans,
          values.type,
          values?.amount || 0,
          values?.propertyTaxesMonthsCollected || (values.type === EnumClosingCostType.propertyTaxes ? 3 : 2),
          values?.daysCollected || 15
        )

        const totalClosingCosts = (closingCosts - oldAmount) + amount;

        const formatedClosingCosts = parseFloat(formatAmount(totalClosingCosts))

        formInstance.setFieldValue(['loans', index, 'closingCosts', 'value'], formatedClosingCosts);

        const loanAmountWithUip = calculateLoanAmountWithUip(form, index, { closingCosts: formatedClosingCosts  })

        form.setFieldValue(['loans', index, 'loanAmountWithUip', 'value'], loanAmountWithUip);

        const downPaymentPrice = calculateDownPaymentPrice(form, index)

        dataToSend.down_payment_price = downPaymentPrice

        formInstance.setFieldValue(['loans', index, 'down_payment_price'], downPaymentPrice)

        const reportClosingCost = reportClosingCosts?.find(cost => cost.text === values.text);

        if (reportClosingCost?.aprIncluded) {
          updateAprAndAprRelatedClosingCost(
            formInstance,
            index,
            oldAmount,
            amount
          )
        }
      }

      const updatedLoan = formInstance.getFieldValue(['loans', index]);

      const principalInterest = calculatePrincipalInterest(updatedLoan);

      form.setFieldValue(['loans', index, 'principalInterest', 'value'], principalInterest);

      await updateLoanClosingCost(dataToSend, id);
    } catch (error) {
      openNotification?.({
        message: 'Error',
        description: "Can't update closing cost",
        type: 'error',
      });
    } finally {
      setPreviewLoading(false)
    }
  };

  const onCreate = async (values: {
    text: string;
    type: EnumClosingCostType;
    amount?: number;
    propertyTaxesMonthsCollected?: number;
    daysCollected?: number;
  }) => {
    if (!reportId) return;
    if (!user.id) return;

    try {
      const dataToSend: any = {
        text: values.text,
        type: values.type,
        active: true,
        reportId,
        userId: Number(user.id),
        amount: values.amount,
        ...(values?.propertyTaxesMonthsCollected &&
          { propertyTaxesMonthsCollected: values.propertyTaxesMonthsCollected }),
        ...(values?.daysCollected && { daysCollected: values.daysCollected }),
      };

      await createReportClosingCost(dataToSend);
    } catch (error) {
      openNotification?.({
        message: 'Error',
        description: "Can't create closing cost",
        type: 'error',
      });
    } finally {
      setPreviewLoading(false)
    }
  };

  const handleFinish = async (values: { text: string; type: EnumClosingCostType; amount?: number }) => {
    if (updateRows && updateRows.length > 0) {
      const editPromises = updateRows.map((row, index) =>
        onEdit(row.closingCostId, row.amount, index, { ...values }).then(() => {
          form.setFieldsValue({
            ...values
          });
        })
      );

      await Promise.all(editPromises);

      const reportClosingCost = reportClosingCosts?.find(cost => cost.text === values.text);

      if (reportClosingCost?.aprIncluded) {
        const updatedLoans: ILoanValue[] = formInstance.getFieldValue('loans');

        await reportLoanUpdate({ loans: prepareLoanDataFromForm(updatedLoans) }, reportId)
      }
    } else {
      await onCreate(values);
      form.resetFields();
    }

    // Fetch latest closing costs immediately
    await fetchLoansClosingCosts(undefined, reportId);

    // Ensure data is updated in table without refresh
    if (refetchData) {
      refetchData();
    }

    if (setPopoverOpen) {
      setPopoverOpen(false);
    }

    if (rowsInitializedRef) {
      setPreviewLoading(true);
      setTimeout(() => {
        // eslint-disable-next-line no-param-reassign
        rowsInitializedRef.current = false;
        setPreviewLoading(false);
      }, 4000);
    }

    getLoans?.()
  };


  const isLoading = createLoading || updateLoading;

  return (
    <Form form={form} layout="vertical" onFinish={handleFinish}>
      <Form.Item
        name="text"
        label="Text"
        rules={[{ required: true, message: 'Please enter the text' }]}
      >
        <Input placeholder="Text" />
      </Form.Item>
      <Form.Item
        name="type"
        label="Type"
        rules={[{ required: true, message: 'Please select the type' }]}
      >
        <Select placeholder="Select Type"
          onChange={(value) => {
            if (value === EnumClosingCostType.prepaidInterest15Days) {
              form.setFields([
                {
                  name: 'amount',
                  errors: [],
                },
              ]);
            }
          }}>
          <Option value={EnumClosingCostType.flatFee}>Flat Fee</Option>
          <Option value={EnumClosingCostType.percentageOfLoanAmount}>Percentage of Loan Amount</Option>
          <Option value={EnumClosingCostType.prepaidInterest15Days}>Prepaid Interest</Option>
          <Option value={EnumClosingCostType.percentageOfPurchasePrice}>Percentage of Purchase Price</Option>
          <Option value={EnumClosingCostType.propertyTaxes}>Property Taxes</Option>
          <Option value={EnumClosingCostType.homeownersInsurance}>Homeowners Insurance</Option>
        </Select>
      </Form.Item>
      <Form.Item shouldUpdate={(prevValues, currentValues) => prevValues.type !== currentValues.type}>
        {({ getFieldValue }) => {
          const type = getFieldValue('type');
          const prefix = (
            type === EnumClosingCostType.flatFee ||
            type === EnumClosingCostType.propertyTaxes ||
            type === EnumClosingCostType.homeownersInsurance
          ) ? '$' : '';
          const addonAfter = (type === EnumClosingCostType.percentageOfLoanAmount
            || type === EnumClosingCostType.percentageOfPurchasePrice) ? '%' : '';

          const isPrepaidInterest = type === EnumClosingCostType.prepaidInterest15Days;
          const isPropertyTaxes = type === EnumClosingCostType.propertyTaxes
          const isHomeownersInsurance = type === EnumClosingCostType.homeownersInsurance

          const isPrefixDollarSign = prefix?.includes('$');

          const handleAmountChange = (event: ChangeEvent<HTMLInputElement>) => {
            const { value } = event.target;

            if (isPrefixDollarSign) {
              form.setFieldValue('amount', value.replace(/[^0-9.-]/g, ''))
            } else {
              form.setFieldValue('amount', value.replace(/[^0-9.-]/g, ''))
            }
          }

          const rules = !isEditMode ? [
            {
              required: !isEditMode,
              message: 'Please enter the amount',
            },
          ] : []

          return (
            <>
              {(isPropertyTaxes || isHomeownersInsurance) && (
                <Form.Item
                  name='propertyTaxesMonthsCollected'
                  label='Months Collected'
                  rules={[{ required: true, message: 'Please select number of months collected' }]}
                  initialValue={isPropertyTaxes ? 3 : 2}
                >
                  <Select placeholder="Select months collected">
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                      <Select.Option key={month} value={month}>
                        {month}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              )}
              {isPrepaidInterest && (
                <Form.Item
                  name='daysCollected'
                  label='Days Collected'
                  rules={[{ required: true, message: 'Please select number of days collected' }]}
                  initialValue={15}
                >
                  <Select placeholder="Select days collected">
                    {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                      <Select.Option key={day} value={day}>
                        {day}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              )}
              {!isPropertyTaxes && !isHomeownersInsurance && !isPrepaidInterest && (
                <Form.Item
                  name="amount"
                  label="Amount"
                  rules={rules}
                >
                  <Input
                    name="amount"
                    placeholder="Enter Amount"
                    prefix={prefix}
                    addonAfter={addonAfter}
                    onChange={handleAmountChange}
                  />
                </Form.Item>
              )}
            </>
          );
        }}
      </Form.Item>
      <Form.Item>
        <Button type="primary" htmlType="submit" loading={isLoading}>
          Save
        </Button>
      </Form.Item>
    </Form>
  );
};

ClosingCostFormPopover.defaultProps = {
  updateRows: [],
  setPopoverOpen: undefined,
  refetchData: undefined,
  isEditMode: false
};

export default ClosingCostFormPopover;
